import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { searchableSelectAutocompleteProps } from '../../../components/SearchableSelect';
import noImputableService, {
  MAX_NO_IMPUTABLE_DESCRIPCION_LEN,
  MAX_NO_IMPUTABLE_NOMBRE_LEN,
  type NoImputable,
  trimNoImputableDescripcion,
  trimNoImputableForFormFields,
  trimNoImputableNombreCuenta,
} from '../../../services/noImputableService';
import { getApiErrorMessage } from '../../../utils/apiError';

const defaultForm: NoImputable = {
  nroRubro: 0,
  descripcioncta: '',
  nombreCuenta: '',
  segmento: 0,
  totalizaEn: undefined,
  saldo: undefined,
};

export interface PlanCuentaNoImputableDrawerProps {
  open: boolean;
  nroRubro?: number | null;
  totalizaEnPreset?: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const PlanCuentaNoImputableDrawer: React.FC<PlanCuentaNoImputableDrawerProps> = ({
  open,
  nroRubro,
  totalizaEnPreset,
  onClose,
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = nroRubro != null && nroRubro > 0;
  const [formData, setFormData] = useState<NoImputable>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rubroOptions, setRubroOptions] = useState<NoImputable[]>([]);

  const load = useCallback(async () => {
    if (!isEdit || nroRubro == null) return;
    try {
      setLoading(true);
      const row = await noImputableService.getNoImputable(nroRubro);
      setFormData(trimNoImputableForFormFields(row));
    } catch {
      enqueueSnackbar('Error al cargar el no imputable', { variant: 'error' });
      onClose();
    } finally {
      setLoading(false);
    }
  }, [isEdit, nroRubro, enqueueSnackbar, onClose]);

  useEffect(() => {
    if (!open) return;
    noImputableService
      .getAllNoImputablesPlanCuentas()
      .then((list) => setRubroOptions(list.sort((a, b) => a.nroRubro - b.nroRubro)))
      .catch(() => {});

    if (isEdit) {
      void load();
      return;
    }
    const base: NoImputable = { ...defaultForm };
    if (totalizaEnPreset != null && totalizaEnPreset > 0) {
      base.totalizaEn = totalizaEnPreset;
    }
    setFormData(base);
  }, [open, isEdit, totalizaEnPreset, load]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nroRubro' || name === 'segmento' || name === 'totalizaEn') {
      const numVal = value === '' ? undefined : Number(value);
      setFormData((prev) => ({ ...prev, [name]: numVal ?? 0 }));
    } else if (name === 'saldo') {
      const numVal = value === '' ? undefined : parseFloat(value);
      setFormData((prev) => ({ ...prev, [name]: numVal }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nroRubro) {
      enqueueSnackbar('El número de rubro es requerido', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (isEdit && nroRubro != null) {
        await noImputableService.updateNoImputable(nroRubro, {
          descripcioncta: formData.descripcioncta,
          nombreCuenta: formData.nombreCuenta,
          segmento: formData.segmento,
          totalizaEn: formData.totalizaEn === 0 ? undefined : formData.totalizaEn,
          saldo: formData.saldo,
        });
      } else {
        await noImputableService.createNoImputable(formData);
      }
      enqueueSnackbar('No imputable guardado exitosamente', { variant: 'success' });
      onSaved();
      onClose();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar el no imputable'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 0 } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
            {isEdit ? 'Editar no imputable' : 'Nuevo no imputable'}
          </Typography>
          <IconButton onClick={onClose} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form id="form-no-imputable-drawer" onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    name="nroRubro"
                    label="Código (Nro. Rubro)"
                    type="number"
                    value={formData.nroRubro || ''}
                    onChange={handleChange}
                    fullWidth
                    required
                    disabled={isEdit}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    name="nombreCuenta"
                    label="Nombre de cuenta"
                    value={formData.nombreCuenta || ''}
                    onChange={handleChange}
                    onBlur={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nombreCuenta: trimNoImputableNombreCuenta(prev.nombreCuenta) ?? '',
                      }))
                    }
                    fullWidth
                    inputProps={{ maxLength: MAX_NO_IMPUTABLE_NOMBRE_LEN }}
                    helperText={`${(formData.nombreCuenta || '').trim().length}/${MAX_NO_IMPUTABLE_NOMBRE_LEN}`}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="descripcioncta"
                    label="Descripción"
                    value={formData.descripcioncta || ''}
                    onChange={handleChange}
                    onBlur={() =>
                      setFormData((prev) => ({
                        ...prev,
                        descripcioncta: trimNoImputableDescripcion(prev.descripcioncta),
                      }))
                    }
                    fullWidth
                    multiline
                    rows={3}
                    inputProps={{ maxLength: MAX_NO_IMPUTABLE_DESCRIPCION_LEN }}
                    helperText={`${(formData.descripcioncta || '').trim().length}/${MAX_NO_IMPUTABLE_DESCRIPCION_LEN}`}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="segmento"
                    label="Segmento (Nivel)"
                    type="number"
                    value={formData.segmento ?? ''}
                    onChange={handleChange}
                    fullWidth
                    inputProps={{ min: 0 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Autocomplete
                    {...searchableSelectAutocompleteProps}
                    options={rubroOptions}
                    getOptionLabel={(opt) =>
                      `${opt.nroRubro} — ${opt.nombreCuenta || opt.descripcioncta || ''}`
                    }
                    value={rubroOptions.find((r) => r.nroRubro === formData.totalizaEn) ?? null}
                    onChange={(_e, val) =>
                      setFormData((prev) => ({
                        ...prev,
                        totalizaEn: val?.nroRubro ?? undefined,
                      }))
                    }
                    isOptionEqualToValue={(opt, val) => opt.nroRubro === val.nroRubro}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Totaliza en"
                        placeholder="Buscar por código o nombre..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="saldo"
                    label="Saldo"
                    type="number"
                    value={formData.saldo ?? ''}
                    onChange={handleChange}
                    fullWidth
                    inputProps={{ step: 0.01 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Button
            type="submit"
            form="form-no-imputable-drawer"
            variant="contained"
            disabled={saving || loading}
            sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
          >
            {saving ? <CircularProgress size={22} /> : 'Guardar'}
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
          >
            Cancelar
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default PlanCuentaNoImputableDrawer;
